import { create } from 'zustand'
import { persist } from 'zustand/middleware'

import { boothAuthHeaders } from '@renderer/utils/boothApiAuth'
import type { PresetEntry } from '@renderer/utils/cameras/presetCompatibility'
import type { CameraBrand } from '@renderer/utils/cameras/captureRiskAssessment'

/**
 * The account's saved camera configurations, one row per project + camera model.
 *
 * Persisted to localStorage for the same reason `featureFlagsStore` is: a booth
 * that cannot reach the network must still be able to open Camera Settings and
 * apply the configuration it already knows about. A failed fetch keeps the last
 * known list rather than blanking it.
 *
 * Writes are skipped locally when the content hash has not moved — the server
 * gates on its own hash too, but not sending the request at all is cheaper and
 * keeps an offline booth from queueing pointless work.
 */

export interface StoredPreset {
  _id: string
  ownerEmail: string
  projectId: string
  cameraModel: string
  cameraBrand: CameraBrand
  entries: PresetEntry[]
  appSettings?: { capture?: Record<string, unknown>; geometry?: Record<string, unknown> }
  entriesHash: string
  entryCount: number
  isPinned?: boolean
  name?: string | null
  backend?: 'wasm' | 'native'
  appVersion?: string
  sourceDeviceId?: string
  createdByEmail?: string
  capturedAt?: string
  updatedAt?: string
}

export interface SavePresetPayload {
  projectId: string
  cameraModel: string
  cameraBrand: CameraBrand
  entries: PresetEntry[]
  appSettings: { capture: Record<string, unknown>; geometry: Record<string, unknown> }
  backend?: 'wasm' | 'native'
  appVersion?: string
  sourceDeviceId?: string
}

interface CameraPresetState {
  presets: StoredPreset[]
  /** projectId -> human title, for the picker. Missing ids fall back to the id. */
  projectNames: Record<string, string>
  loaded: boolean
  lastFetch: number | null
  /** `${projectId}|${cameraModel}` -> last hash this booth successfully sent. */
  sentHashes: Record<string, string>

  fetchPresets: (online?: boolean) => Promise<void>
  savePreset: (payload: SavePresetPayload) => Promise<'saved' | 'unchanged' | 'failed'>
  /** Resolves `null` on success, or a short reason ('403', 'offline', …) to show. */
  setPinned: (id: string, isPinned: boolean, name?: string | null) => Promise<string | null>
  deletePreset: (id: string) => Promise<boolean>
  clear: () => void
}

const API = (): string => process.env.VITE_API_URL || ''

/**
 * Content hash, mirroring `hashPresetContent` on the server (sorted name/value
 * pairs + app settings). It only has to be STABLE and to agree with itself
 * across runs — the server recomputes its own, authoritative one — so a compact
 * non-cryptographic digest is enough here and avoids pulling in SubtleCrypto's
 * async API on a synchronous path.
 */
export function localContentHash(
  entries: PresetEntry[],
  appSettings: { capture: unknown; geometry: unknown }
): string {
  const canonical = JSON.stringify({
    entries: [...entries]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map((e) => [e.name, e.value]),
    capture: appSettings.capture,
    geometry: appSettings.geometry
  })
  let h1 = 0x811c9dc5
  let h2 = 0x01000193
  for (let i = 0; i < canonical.length; i++) {
    const c = canonical.charCodeAt(i)
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0
    h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0
  }
  return `${h1.toString(16)}${h2.toString(16)}`
}

export const useCameraPresetStore = create<CameraPresetState>()(
  persist(
    (set, get) => ({
      presets: [],
      projectNames: {},
      loaded: false,
      lastFetch: null,
      sentHashes: {},

      fetchPresets: async (online = true): Promise<void> => {
        if (!online) {
          set({ loaded: true })
          return
        }
        try {
          const res = await fetch(`${API()}/api/camera-presets`, {
            credentials: 'include',
            headers: await boothAuthHeaders()
          })
          if (!res.ok) {
            // Unauthenticated / server error: keep the last-known list. Blanking
            // it would take the feature away from a booth that is merely offline.
            console.warn('[CAMERA-PRESET] fetch failed:', res.status)
            set({ loaded: true })
            return
          }
          const data = (await res.json()) as {
            presets?: StoredPreset[]
            projectNames?: Record<string, string>
          }
          set({
            presets: Array.isArray(data.presets) ? data.presets : [],
            projectNames: data.projectNames || {},
            loaded: true,
            lastFetch: Date.now()
          })
        } catch (error) {
          console.warn('[CAMERA-PRESET] fetch error:', error)
          set({ loaded: true })
        }
      },

      savePreset: async (payload): Promise<'saved' | 'unchanged' | 'failed'> => {
        const key = `${payload.projectId}|${payload.cameraModel}`
        const hash = localContentHash(payload.entries, payload.appSettings)
        if (get().sentHashes[key] === hash) return 'unchanged'

        try {
          const res = await fetch(`${API()}/api/camera-presets`, {
            method: 'PUT',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(await boothAuthHeaders())
            },
            body: JSON.stringify(payload)
          })
          if (!res.ok) {
            console.warn('[CAMERA-PRESET] save failed:', res.status)
            return 'failed'
          }
          const data = (await res.json()) as { changed?: boolean; reason?: string }
          // Record the hash either way: an unchanged/pinned response means this
          // exact content is already what the server holds, so re-sending it is
          // pointless until the operator changes something.
          set((state) => ({ sentHashes: { ...state.sentHashes, [key]: hash } }))
          if (data.changed) {
            void get().fetchPresets()
            return 'saved'
          }
          return 'unchanged'
        } catch (error) {
          console.warn('[CAMERA-PRESET] save error:', error)
          return 'failed'
        }
      },

      /**
       * Returns the FAILURE REASON rather than a bare false. A pinned row is the
       * only defence against auto-snapshot overwriting a configuration an
       * operator wants kept, so "could not save" with no detail leaves both them
       * and us guessing: 401 is a lapsed token, 403 is tenant ownership, 404 is
       * project scope, 5xx is ours. The operator can read it off the toast.
       */
      setPinned: async (id, isPinned, name): Promise<string | null> => {
        try {
          const res = await fetch(`${API()}/api/camera-presets/${id}`, {
            method: 'PATCH',
            credentials: 'include',
            headers: {
              'Content-Type': 'application/json',
              ...(await boothAuthHeaders())
            },
            body: JSON.stringify({ isPinned, ...(name !== undefined ? { name } : {}) })
          })
          if (!res.ok) {
            const body = await res.text().catch(() => '')
            console.warn('[CAMERA-PRESET] pin failed:', res.status, body.slice(0, 200))
            return String(res.status)
          }
          set((state) => ({
            presets: state.presets.map((p) =>
              p._id === id ? { ...p, isPinned, name: isPinned ? (name ?? p.name) : null } : p
            )
          }))
          return null
        } catch (error) {
          console.warn('[CAMERA-PRESET] pin error:', error)
          return 'offline'
        }
      },

      deletePreset: async (id): Promise<boolean> => {
        try {
          const res = await fetch(`${API()}/api/camera-presets/${id}`, {
            method: 'DELETE',
            credentials: 'include',
            headers: await boothAuthHeaders()
          })
          if (!res.ok) return false
          set((state) => ({ presets: state.presets.filter((p) => p._id !== id) }))
          return true
        } catch {
          return false
        }
      },

      clear: (): void =>
        void set({ presets: [], projectNames: {}, sentHashes: {}, loaded: false, lastFetch: null })
    }),
    {
      name: 'camera-presets-v1',
      partialize: (state) => ({
        presets: state.presets,
        projectNames: state.projectNames,
        sentHashes: state.sentHashes,
        lastFetch: state.lastFetch
      })
    }
  )
)
